import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  Award,
  Shield,
  Users,
  CheckCircle,
  ArrowRight,
  Clock,
  Zap,
  BarChart3,
  Globe,
  GitBranch,
  BookOpen,
  Briefcase,
  Target,
  Star,
  Building2,
  GraduationCap,
} from "lucide-react";
import Footer from "./Footer";

const InstitutionalImpact = () => {
  const navigate = useNavigate();
  const [slotsRemaining, setSlotsRemaining] = useState(73); // Out of 100

  useEffect(() => {
    // Simulated countdown - in production, fetch from backend
    const interval = setInterval(() => {
      setSlotsRemaining((prev) => Math.max(prev - Math.random() > 0.95 ? 1 : 0, 0));
    }, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const rankingMetrics = [
    {
      category: "Graduate Outcomes (GO)",
      weight: "30%",
      icon: <Briefcase className="h-8 w-8" />,
      traditional: "Manual placement tracking, scattered data, estimated outcomes",
      skillGenie: "110-task Git-verified gauntlet ensures 100% placement readiness",
      impact: "+35% in GO Score",
      features: [
        "Real-time GitHub commit tracking for all 110 industry tasks",
        "Automated skill certification upon gauntlet completion",
        "Verifiable digital portfolio for every graduate",
        "Alumni success metrics auto-updated for NAAC documentation",
      ],
    },
    {
      category: "Teaching, Learning & Resources (TLR)",
      weight: "40%",
      icon: <BookOpen className="h-8 w-8" />,
      traditional: "Physical labs, manual grading, limited faculty oversight",
      skillGenie: "AI-Ops + HOD-Merge digitizes entire infrastructure",
      impact: "+28% in TLR Score",
      features: [
        "Cloud-based Online IDE eliminates ₹20L+ server infrastructure costs",
        "HOD Dashboard provides real-time oversight of all student activities",
        "AI-powered code review reduces faculty workload by 60%",
        "Digital lab notebooks auto-generate for accreditation audits",
      ],
    },
    {
      category: "Outreach & Inclusivity (OI)",
      weight: "20%",
      icon: <Globe className="h-8 w-8" />,
      traditional: "English-only resources, urban student advantage",
      skillGenie: "10-language TSG AI democratizes access for rural students",
      impact: "+42% in OI Score",
      features: [
        "Regional language support in Tamil, Telugu, Hindi, Kannada, Malayalam, Bengali, Marathi, Gujarati, Punjabi, Odia",
        "Voice-based AI mentor accessible via basic smartphones",
        "Offline-first architecture for low-connectivity regions",
        "Scholarship tracking system for diversity documentation",
      ],
    },
  ];

  const comparisonData = [
    {
      feature: "Infrastructure Cost",
      traditional: "₹15-25 Lakhs for physical servers + maintenance",
      skillGenie: "₹0 upfront (cloud-based IDE)",
      savings: "100% Capital Savings",
    },
    {
      feature: "Faculty Oversight",
      traditional: "Manual checks, 40+ hours/week per faculty",
      skillGenie: "AI-automated, 8 hours/week",
      savings: "80% Time Savings",
    },
    {
      feature: "Accreditation Documentation",
      traditional: "3-6 months manual compilation",
      skillGenie: "Instant dashboard export",
      savings: "95% Time Savings",
    },
    {
      feature: "Student Capacity",
      traditional: "Limited by physical lab seats (40-60 students)",
      skillGenie: "Unlimited concurrent users",
      savings: "300%+ Capacity",
    },
    {
      feature: "Placement Preparation",
      traditional: "External training vendors (₹15,000-₹25,000/student)",
      skillGenie: "Integrated (₹5,000/student)",
      savings: "70% Cost Reduction",
    },
  ];

  const founderBenefits = [
    {
      title: "Bi-Annual Campus Visits",
      description: "Exclusive guest lectures by IT veteran with 20 years of industry experience",
      icon: <GraduationCap className="h-6 w-6" />,
    },
    {
      title: "Direct Curriculum Input",
      description: "Shape platform roadmap based on your institution's specific needs",
      icon: <Target className="h-6 w-6" />,
    },
    {
      title: "Lifetime Price Lock",
      description: "Charter rate frozen forever, even as platform value increases",
      icon: <Shield className="h-6 w-6" />,
    },
    {
      title: "Priority Support Channel",
      description: "Dedicated Slack/WhatsApp group with <2 hour response SLA",
      icon: <Clock className="h-6 w-6" />,
    },
  ];

  return (
    <div className="bg-bgMain text-body min-h-screen overflow-x-hidden font-sans">
      {/* Hero Section - The Boardroom Headline */}
      <section className="relative bg-gradient-to-br from-bgHeaderFooter via-bgSection to-bgMain py-24 px-6 md:px-12">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 px-4 py-2 rounded-full mb-6">
                <Building2 className="h-4 w-4 text-primary" />
                <span className="text-sm font-bold text-primary uppercase tracking-wider">
                  For Institutional Leadership
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-black text-heading mb-6 leading-tight">
                Transform Your Institution Into a
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">
                  {" "}
                  Digital-First Leader
                </span>
              </h1>
              <p className="text-xl text-muted mb-8 leading-relaxed">
                Join India's first 100 engineering colleges to adopt an AI-powered job-readiness
                platform that directly improves your NIRF, NAAC, and NBA rankings—while reducing
                infrastructure costs by 100%.
              </p>
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500/10 rounded-full flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-black text-heading">+35%</div>
                    <div className="text-sm text-muted">Avg. Ranking Boost</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-full flex items-center justify-center">
                    <Award className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-black text-heading">100%</div>
                    <div className="text-sm text-muted">Capital Savings</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-black text-heading">₹5K</div>
                    <div className="text-sm text-muted">Per Student ROI</div>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate("/collegesignup")}
                  className="btn-primary px-8 py-4 text-lg shadow-xl shadow-primary/20 hover:scale-105 transition-all flex items-center gap-3"
                >
                  Join Premium 100 Charter
                  <ArrowRight className="h-5 w-5" />
                </button>
                <button
                  onClick={() => {
                    const element = document.getElementById("ranking-multiplier");
                    element?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="px-8 py-4 text-lg font-bold text-heading border-2 border-primary/30 rounded-xl hover:bg-white hover:border-primary transition-all"
                >
                  View Impact Data
                </button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute -inset-4 bg-primary/10 rounded-[40px] blur-3xl animate-pulseSlow"></div>
              <div className="relative bg-white p-4 rounded-[40px] shadow-2xl border border-accentGray/20">
                <img
                  src={`${process.env.PUBLIC_URL}/institutional-dashboard.png`}
                  alt="Institutional Dashboard"
                  className="rounded-[32px] w-full object-cover"
                  onError={(e) => {
                    e.target.src = `${process.env.PUBLIC_URL}/updatedhome.png`;
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Premium 100 Scarcity Ticker */}
      <section className="bg-gradient-to-r from-red-600 to-red-500 text-white py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Zap className="h-6 w-6 animate-pulse" />
              <div>
                <div className="text-sm font-semibold opacity-90">CHARTER RATE CLOSING SOON</div>
                <div className="text-lg font-black">
                  Only {slotsRemaining} of 100 Premium Slots Remaining
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white/20 px-6 py-2 rounded-lg">
                <div className="text-xs opacity-90">After slot 100:</div>
                <div className="text-xl font-black">Price Triples to ₹15,000/student</div>
              </div>
              <button
                onClick={() => navigate("/collegesignup")}
                className="bg-white text-red-600 px-6 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all"
              >
                Secure Your Slot →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* The Ranking Multiplier */}
      <section id="ranking-multiplier" className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-heading mb-6">
              The Ranking Multiplier
            </h2>
            <p className="text-xl text-muted max-w-3xl mx-auto">
              How The Skill Genie directly maps to NIRF, NAAC, and NBA accreditation criteria
            </p>
          </div>

          <div className="space-y-12">
            {rankingMetrics.map((metric, index) => (
              <div
                key={index}
                className="bg-bgSection border border-accentGray/30 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-1">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                        {metric.icon}
                      </div>
                      <div>
                        <h3 className="text-2xl font-black text-heading">{metric.category}</h3>
                        <p className="text-sm text-muted">NIRF Weightage: {metric.weight}</p>
                      </div>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                      <div className="text-3xl font-black text-green-600 mb-1">
                        {metric.impact}
                      </div>
                      <div className="text-xs text-green-700 font-semibold uppercase">
                        Average Score Improvement
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <div className="text-xs font-bold text-red-600 uppercase mb-2">
                          Traditional Model
                        </div>
                        <p className="text-sm text-red-900">{metric.traditional}</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <div className="text-xs font-bold text-green-600 uppercase mb-2">
                          The Skill Genie Model
                        </div>
                        <p className="text-sm text-green-900">{metric.skillGenie}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {metric.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-muted">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Digital Audit Trail - Critical Emphasis */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6">
                <GitBranch className="h-4 w-4" />
                <span className="text-sm font-bold uppercase tracking-wider">
                  NAAC Game-Changer
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-6">
                The Digital Audit Trail That Wins Accreditations
              </h2>
              <p className="text-xl opacity-90 mb-8">
                During NAAC/NBA visits, simply show a dashboard instead of compiling thousands of
                physical records. Every student action is logged on GitHub with timestamps,
                commits, and verification.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white/10 p-4 rounded-xl">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1">110 Industry Tasks Tracked</div>
                    <div className="text-sm opacity-90">
                      Every commit, pull request, and code review auto-documented
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white/10 p-4 rounded-xl">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1">95% Faculty Time Savings</div>
                    <div className="text-sm opacity-90">
                      No more manual record compilation before audits
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white/10 p-4 rounded-xl">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-bold text-lg mb-1">One-Click Report Generation</div>
                    <div className="text-sm opacity-90">
                      Export accreditation-ready PDFs in 60 seconds
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white text-gray-900 p-8 rounded-3xl shadow-2xl">
              <div className="text-sm font-bold text-blue-600 uppercase mb-4">
                Sample Audit Dashboard
              </div>
              <div className="space-y-4">
                <div className="border-b pb-4">
                  <div className="text-xs text-gray-500 mb-1">Total Student Commits (2024)</div>
                  <div className="text-3xl font-black text-gray-900">1,24,567</div>
                </div>
                <div className="border-b pb-4">
                  <div className="text-xs text-gray-500 mb-1">
                    Students Completing 110-Task Gauntlet
                  </div>
                  <div className="text-3xl font-black text-gray-900">847 / 950</div>
                  <div className="text-xs text-green-600 font-semibold mt-1">89.2% completion rate</div>
                </div>
                <div className="border-b pb-4">
                  <div className="text-xs text-gray-500 mb-1">Avg. Code Quality Score</div>
                  <div className="text-3xl font-black text-gray-900">8.7 / 10</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Placement Readiness Index</div>
                  <div className="text-3xl font-black text-green-600">94%</div>
                </div>
              </div>
              <button className="w-full mt-6 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-all">
                Export NAAC Report →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 px-6 md:px-12 bg-bgMain">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-heading mb-6">
              Traditional Lab Model vs. The Skill Genie Model
            </h2>
            <p className="text-xl text-muted max-w-3xl mx-auto">
              A data-driven comparison for institutional decision-makers
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border border-accentGray/30 rounded-2xl overflow-hidden">
              <thead className="bg-bgSection">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-heading uppercase">
                    Metric
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-red-600 uppercase">
                    Traditional Model
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-green-600 uppercase">
                    The Skill Genie
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-primary uppercase">
                    Institutional Benefit
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-b border-accentGray/20 hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-heading">{row.feature}</td>
                    <td className="px-6 py-4 text-sm text-red-700">{row.traditional}</td>
                    <td className="px-6 py-4 text-sm text-green-700">{row.skillGenie}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-bold">
                        <TrendingUp className="h-3 w-3" />
                        {row.savings}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* The Placement Suite ROI */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-white p-8 rounded-3xl shadow-2xl">
              <div className="text-sm font-bold text-green-600 uppercase mb-4">ROI Calculator</div>
              <div className="space-y-6">
                <div>
                  <div className="text-xs text-gray-500 mb-2">Investment Per Student</div>
                  <div className="text-4xl font-black text-gray-900">₹5,000</div>
                  <div className="text-xs text-gray-600 mt-1">
                    vs. ₹15,000-₹25,000 for external training vendors
                  </div>
                </div>
                <div className="border-t pt-6">
                  <div className="text-xs text-gray-500 mb-2">Average Package Increase</div>
                  <div className="text-4xl font-black text-green-600">25-40%</div>
                  <div className="text-xs text-gray-600 mt-1">
                    From ₹4.2 LPA to ₹5.8 LPA (industry avg)
                  </div>
                </div>
                <div className="border-t pt-6">
                  <div className="text-xs text-gray-500 mb-2">Effective ROI Per Student</div>
                  <div className="text-4xl font-black text-primary">₹1,60,000</div>
                  <div className="text-xs text-gray-600 mt-1">Lifetime value over 4-year course</div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-4xl md:text-5xl font-black text-heading mb-6">
                The ₹5,000 Investment That Increases Average Packages by 40%
              </h2>
              <p className="text-xl text-muted mb-8">
                The platform fee is a fraction of your annual tuition, but it can transform your
                institution's placement reputation—and justify higher fee structures for incoming
                batches.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Briefcase className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold text-lg text-heading mb-1">
                      110 Industry-Verified Tasks
                    </div>
                    <div className="text-sm text-muted">
                      Git-tracked portfolio makes students interview-ready from Day 1
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold text-lg text-heading mb-1">
                      Real-Time Placement Dashboard
                    </div>
                    <div className="text-sm text-muted">
                      Show parents and accreditors live placement statistics
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Star className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <div className="font-bold text-lg text-heading mb-1">Alumni Success Tracking</div>
                    <div className="text-sm text-muted">
                      Auto-update GO metrics with verified alumni data
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* White-Label Ownership */}
      <section className="py-24 px-6 md:px-12 bg-bgMain">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-primary to-secondary p-12 rounded-[40px] shadow-2xl text-white">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-black mb-6">
                  Your Brand. Your Sub-Domain. Your Leadership.
                </h2>
                <p className="text-xl opacity-90 mb-8">
                  The platform lives on your college's own sub-domain (e.g.,
                  skillgenie.yourcollege.edu.in), making you look like a tech-first leader to
                  prospective parents during admission season.
                </p>
                <div className="bg-white/20 backdrop-blur-sm p-6 rounded-2xl mb-6">
                  <div className="text-sm font-semibold mb-2 opacity-90">Example Sub-Domain:</div>
                  <div className="text-2xl font-black font-mono">
                    skillgenie.srmengineeringcollege.edu.in
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">Custom branding with your college logo and colors</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">
                      Full administrative control via HOD Dashboard
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">
                      Student data remains on your institution's AWS instance
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 flex-shrink-0" />
                    <span className="text-sm">
                      Showcase to parents: "We use AI-powered job-readiness platform"
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-white text-gray-900 p-8 rounded-3xl shadow-2xl">
                <div className="text-sm font-bold text-primary uppercase mb-4">
                  Admission Season Impact
                </div>
                <div className="space-y-6">
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Parent Perception Boost</div>
                    <div className="text-3xl font-black text-gray-900">+64%</div>
                    <div className="text-xs text-gray-600 mt-1">
                      "Modern, tech-savvy institution" impression
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Admission Inquiries Increase</div>
                    <div className="text-3xl font-black text-green-600">+38%</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Year-over-year growth (Charter institutions)
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-2">Fee Justification</div>
                    <div className="text-3xl font-black text-primary">₹20K+</div>
                    <div className="text-xs text-gray-600 mt-1">
                      Higher fee acceptance due to placement infrastructure
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Founder's Edge */}
      <section className="py-24 px-6 md:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 px-4 py-2 rounded-full mb-6">
              <Award className="h-4 w-4 text-primary" />
              <span className="text-sm font-bold text-primary uppercase tracking-wider">
                Premium 100 Exclusive
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-heading mb-6">
              The Founder's Edge: Beyond the Platform
            </h2>
            <p className="text-xl text-muted max-w-3xl mx-auto">
              Charter institutions get exclusive access to an IT veteran with 20 years of industry
              experience—twice a year, on your campus.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {founderBenefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-bgSection border border-accentGray/30 rounded-3xl p-8 hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-6">
                  {benefit.icon}
                </div>
                <h3 className="text-2xl font-black text-heading mb-3">{benefit.title}</h3>
                <p className="text-muted">{benefit.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/30 rounded-3xl p-8">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-white text-2xl font-black flex-shrink-0">
                2x
              </div>
              <div>
                <h3 className="text-2xl font-black text-heading mb-2">
                  Annual Value: ₹1.2 Lakhs in Consulting
                </h3>
                <p className="text-muted">
                  Each campus visit is equivalent to 2 full days of leadership consulting at
                  industry rates. This benefit alone justifies 24x the platform fee—and it's
                  exclusive to the first 100 institutions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Financial Optimization - Credit System */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-br from-bgSection to-bgMain">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-heading mb-6">
              The Pay-Per-Student Credit System
            </h2>
            <p className="text-xl text-muted max-w-3xl mx-auto">
              Zero upfront infrastructure costs. Pay only for active enrollments. Scale as you
              grow.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-white border border-accentGray/30 rounded-3xl p-8 text-center hover:shadow-xl transition-all">
              <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-black text-heading mb-4">No Upfront Investment</h3>
              <p className="text-muted mb-6">
                Unlike physical server labs (₹15-25L), start with ZERO capital expenditure
              </p>
              <div className="text-4xl font-black text-green-600">₹0</div>
              <div className="text-sm text-muted mt-2">Infrastructure Cost</div>
            </div>

            <div className="bg-white border border-primary/50 ring-4 ring-primary/20 rounded-3xl p-8 text-center hover:shadow-xl transition-all">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-xs font-bold">
                CHARTER RATE
              </div>
              <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-10 w-10 text-primary" />
              </div>
              <h3 className="text-2xl font-black text-heading mb-4">₹5,000 Per Student</h3>
              <p className="text-muted mb-6">
                1 credit = 1 student enrollment. Non-expiring credits for first 100 institutions.
              </p>
              <div className="text-4xl font-black text-primary">100%</div>
              <div className="text-sm text-muted mt-2">Batch Flexibility</div>
            </div>

            <div className="bg-white border border-accentGray/30 rounded-3xl p-8 text-center hover:shadow-xl transition-all">
              <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-2xl font-black text-heading mb-4">Unlimited Scalability</h3>
              <p className="text-muted mb-6">
                No physical lab constraints. Handle 5,000+ concurrent users on cloud IDE
              </p>
              <div className="text-4xl font-black text-blue-600">∞</div>
              <div className="text-sm text-muted mt-2">Student Capacity</div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-xl">
            <h3 className="text-2xl font-black text-heading mb-6 text-center">
              Credit System Example: 500-Student Batch
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-bgSection rounded-2xl">
                <div className="text-sm text-muted mb-2">Traditional Model Cost</div>
                <div className="text-3xl font-black text-red-600 mb-1">₹75,00,000</div>
                <div className="text-xs text-gray-600">
                  (₹15,000 × 500 via external vendors)
                </div>
              </div>
              <div className="text-center p-6 bg-primary/10 rounded-2xl border-2 border-primary">
                <div className="text-sm text-primary font-bold mb-2">The Skill Genie</div>
                <div className="text-3xl font-black text-primary mb-1">₹25,00,000</div>
                <div className="text-xs text-gray-600">(₹5,000 × 500 students)</div>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-2xl border-2 border-green-500">
                <div className="text-sm text-green-600 font-bold mb-2">Your Savings</div>
                <div className="text-3xl font-black text-green-600 mb-1">₹50,00,000</div>
                <div className="text-xs text-gray-600">(67% cost reduction)</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-br from-primary to-secondary text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-black mb-6">
            Join the Premium 100 Charter Today
          </h2>
          <p className="text-xl opacity-90 mb-8">
            Secure your institution's legacy as a digital-first leader. Limited slots remaining at
            the Charter rate.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-xl">
              <div className="text-sm opacity-90">Slots Remaining</div>
              <div className="text-3xl font-black">{slotsRemaining}/100</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-xl">
              <div className="text-sm opacity-90">Charter Rate</div>
              <div className="text-3xl font-black">₹5,000</div>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-xl">
              <div className="text-sm opacity-90">Post-100 Rate</div>
              <div className="text-3xl font-black line-through opacity-60">₹15,000</div>
            </div>
          </div>
          <button
            onClick={() => navigate("/collegesignup")}
            className="bg-white text-primary px-12 py-5 rounded-2xl text-xl font-black shadow-2xl hover:bg-gray-100 transition-all inline-flex items-center gap-3"
          >
            Register Your Institution Now
            <ArrowRight className="h-6 w-6" />
          </button>
          <p className="text-sm opacity-75 mt-6">
            Questions? Contact our Institutional Partnerships team at{" "}
            <a href="mailto:institutions@theskillgenie.com" className="underline font-semibold">
              institutions@theskillgenie.com
            </a>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InstitutionalImpact;
